"use client";

import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { FormEvent, useEffect, useRef, useState } from "react";
import { authenticationServiceUnavailableError } from "../../lib/authLoginFailure";
import SessionRevocationReviewBanner from "../components/SessionRevocationReviewBanner";
import { hasSessionRevocationWarning } from "../../lib/sessionRevocationWarning";

// Includes password/provider checks and the bounded SMTP delivery, not just Auth.
const loginRequestTimeoutMs = 60_000;

export default function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);
  const [retrySeconds, setRetrySeconds] = useState(0);
  useEffect(() => {
    if (!retrySeconds) return;
    const timer = setTimeout(() => setRetrySeconds((seconds) => Math.max(0, seconds - 1)), 1_000);
    return () => clearTimeout(timer);
  }, [retrySeconds]);
  const successMessage =
    searchParams.get("passwordReset") === "success"
      ? "Your password has been reset. Sign in with your new password."
      : searchParams.get("activated") === "success"
        ? "Your account is active. Sign in to continue securely."
      : "";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current || retrySeconds > 0) return;
    // Browser autofill can populate the form before React receives a change event.
    const credentials = new FormData(event.currentTarget);
    inFlight.current = true;
    setSubmitting(true);
    setError("");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), loginRequestTimeoutMs);
    let navigating = false;

    try {
      const response = await fetch("/api/customer/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: String(credentials.get("username") || ""), password: String(credentials.get("password") || "") }),
        signal: controller.signal,
      });
      const result = await response.json();

      if (!response.ok) {
        if (response.status === 429 || response.status === 503) {
          const retry = Number(result.retryAfterSeconds || response.headers.get("Retry-After") || 60);
          setRetrySeconds(Number.isFinite(retry) ? Math.max(1, Math.min(300, Math.ceil(retry))) : 60);
        }
        setError(
          response.status === 429 || response.status === 503
            ? result.error || authenticationServiceUnavailableError
            : "Invalid username or password.",
        );
        return;
      }

      const redirectTo = ["/portal", "/verify-email", "/mfa/setup", "/mfa/verify"].includes(
        result.redirectTo,
      )
        ? result.redirectTo
        : "/verify-email";
      navigating = true;
      router.replace(redirectTo === "/portal" && hasSessionRevocationWarning("customer") ? "/portal/account#account-security" : redirectTo);
      router.refresh();
    } catch {
      setError(authenticationServiceUnavailableError);
      setRetrySeconds(60);
    } finally {
      clearTimeout(timeout);
      if (!navigating) {
        inFlight.current = false;
        setSubmitting(false);
      }
      setPassword("");
    }
  }

  return (
    <form
      action="/api/customer/login"
      method="post"
      onSubmit={handleSubmit}
      aria-busy={submitting}
      className="rounded-md border border-[var(--vintera-border)] bg-white p-6"
    >
      <div className="border-b border-[var(--vintera-border)] pb-5">
        <h2 className="text-2xl font-bold text-[var(--vintera-navy)]">
          Customer login
        </h2>
        <p className="mt-2 leading-7 text-[var(--vintera-muted)]">
          Enter your Vintera username and password.
        </p>
      </div>
      <SessionRevocationReviewBanner accountKind="customer" />

      <label className="mt-6 block">
        <span className="text-sm font-semibold text-[var(--vintera-navy)]">
          Username
        </span>
        <input
          name="username"
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          autoComplete="username"
          disabled={submitting}
          required
          className="mt-2 w-full rounded-md border border-[var(--vintera-border)] px-4 py-3 text-[var(--vintera-ink)] outline-none transition focus:border-[var(--vintera-navy)]"
        />
      </label>

      <label className="mt-5 block">
        <span className="text-sm font-semibold text-[var(--vintera-navy)]">
          Password
        </span>
        <input
          name="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          autoComplete="current-password"
          disabled={submitting}
          required
          className="mt-2 w-full rounded-md border border-[var(--vintera-border)] px-4 py-3 text-[var(--vintera-ink)] outline-none transition focus:border-[var(--vintera-navy)]"
        />
      </label>

      {error && (
        <p role="alert" className="mt-5 rounded-md bg-red-100 p-4 text-sm font-medium text-red-800">
          {error}
        </p>
      )}

      {successMessage && (
        <p className="mt-5 rounded-md bg-[var(--vintera-sage-soft)] p-4 text-sm font-medium text-[var(--vintera-navy)]">
          {successMessage}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting || retrySeconds > 0}
        className="mt-6 w-full rounded-md bg-[var(--vintera-navy)] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[var(--vintera-navy-hover)] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Signing in securely…" : retrySeconds > 0 ? `Try again in ${retrySeconds}s` : "Log In"}
      </button>

      <div className="mt-5 flex flex-col gap-2 text-sm sm:flex-row sm:justify-between">
        <Link
          href="/forgot-username"
          className="font-semibold text-[var(--vintera-navy)] hover:underline"
        >
          Forgot Username?
        </Link>
        <Link
          href="/forgot-password"
          className="font-semibold text-[var(--vintera-navy)] hover:underline"
        >
          Forgot Password?
        </Link>
      </div>
    </form>
  );
}
